import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Home, Users, Target, DollarSign, Shield, FileText, MessageSquare,
    Video, Heart, Building2, Briefcase, Store, PenTool, BarChart3,
    ArrowRight, Clock, CheckCircle, ChevronDown, ChevronUp, Search,
    Zap, Globe, Bell
} from 'lucide-react';

const SECTIONS = [
    {
        id: 'day91',
        title: 'The Day 91 Marketplace',
        icon: Store,
        color: 'amber',
        desc: 'Our core system — when a home doesn\'t sell after 90 days, VisionAire steps in.',
        features: [
            {
                title: 'Seller Property Submission',
                desc: 'Sellers submit their property details including address, financials, days on market, and listing agent information. The system auto-calculates equity, projected income, and motivation score.',
                path: '/seller/intake',
                roles: ['seller'],
            },
            {
                title: 'Day 91 Auto-Activation',
                desc: 'Properties submitted with fewer than 91 days on market are stored as "Pending". Once they reach Day 91, they automatically activate on the marketplace. Agents are notified at Day 75 to enroll.',
                path: null,
                roles: ['admin'],
            },
            {
                title: 'Public Marketplace',
                desc: 'Browse all Day 91+ active listings. View property details, owner-finance terms, and agent contact info. The marketplace includes an embedded video explaining the VisionAire concept.',
                path: '/marketplace',
                roles: ['all'],
            },
            {
                title: 'Listing Status Pipeline',
                desc: 'Every listing moves through: Pending (< Day 91) > Active (Day 91+) > Matched (buyer assigned) > Closed. Admin can trigger manual Day 91 checks and view pipeline stats.',
                path: '/admin',
                roles: ['admin'],
            },
        ],
    },
    {
        id: 'roles',
        title: 'User Roles & Portals',
        icon: Users,
        color: 'blue',
        desc: 'Four distinct roles, each with their own dashboard and capabilities.',
        features: [
            {
                title: 'Buyer Portal',
                desc: 'Complete financial intake (income, debts, employment, credit score). Get pre-qualified with an automated scoring engine. View matched properties and deal details.',
                path: '/buyer/intake',
                roles: ['buyer'],
            },
            {
                title: 'Seller Portal',
                desc: 'Submit property with full financials and agent info. See projected owner-finance income, holding cost analysis, and Day 91 countdown timer on your dashboard.',
                path: '/seller/intake',
                roles: ['seller'],
            },
            {
                title: 'Agent Portal',
                desc: 'Enroll listings at Day 75+. Track all enrolled properties with DOM countdown, activation status, views, and inquiry counts. Add new listings with a quick-fill modal.',
                path: '/agent',
                roles: ['agent'],
            },
            {
                title: 'Operator Dashboard',
                desc: 'See only Day 91+ active listings. Your mission: bring buyers. View agent contact info, assign buyers to listings, and track all your assignments with status.',
                path: '/operator',
                roles: ['operator'],
            },
        ],
    },
    {
        id: 'deals',
        title: 'Deal Management',
        icon: FileText,
        color: 'emerald',
        desc: 'Full deal lifecycle from match to close.',
        features: [
            {
                title: 'AI Deal Structuring',
                desc: 'GPT-5.2 powered analysis generates optimal deal structures — purchase price, down payment, interest rate, monthly payment, and balloon terms based on buyer and seller profiles.',
                path: '/dashboard',
                roles: ['buyer', 'seller', 'admin'],
            },
            {
                title: 'Buyer-Seller Matching',
                desc: 'Automated matching engine scores compatibility between buyer financial profiles and seller properties. Admin can view all matches and push deals forward.',
                path: '/admin',
                roles: ['admin'],
            },
            {
                title: 'E-Signatures',
                desc: 'Draw-to-sign canvas for both buyer and seller. Signature data is captured and stored per deal. Status tracking shows who has signed.',
                path: null,
                roles: ['buyer', 'seller'],
            },
            {
                title: 'Payment Tracking',
                desc: 'Monthly payment ledger with late fee detection. Track each payment\'s status, amount, due date, and overdue calculations.',
                path: null,
                roles: ['buyer', 'seller', 'admin'],
            },
            {
                title: 'Video Negotiation Room',
                desc: 'Jitsi-powered video conferencing for live deal negotiations. Create rooms, share meeting links, take notes, and track meeting history.',
                path: null,
                roles: ['buyer', 'seller', 'admin'],
            },
        ],
    },
    {
        id: 'credit',
        title: 'Credit Repair Suite',
        icon: Shield,
        color: 'emerald',
        desc: 'Full credit repair program with AI-powered dispute generation.',
        features: [
            {
                title: '3-Tier Programs',
                desc: 'Basic ($49/mo, 12 months), Accelerated ($99/mo, 6 months), Premium ($149/mo, 4 months). Each tier includes dispute filing, score monitoring, and educational resources.',
                path: '/credit-repair/intake',
                roles: ['buyer', 'seller'],
            },
            {
                title: 'AI Dispute Generation',
                desc: 'GPT-5.2 generates legally-formatted dispute letters for each bureau. Upload credit reports (PDF), and the AI extracts accounts, balances, and creates targeted disputes.',
                path: '/credit-repair',
                roles: ['buyer', 'seller'],
            },
            {
                title: 'PDF Credit Report Parsing',
                desc: 'Upload your credit report PDF. The system uses PDFPlumber/PDFMiner to extract account names, balances, statuses, and payment history automatically.',
                path: '/credit-repair',
                roles: ['buyer', 'seller'],
            },
            {
                title: 'Gamification & Progress',
                desc: 'Graduation countdown timer with circular progress, milestone tracker (enrolled > audit > disputes > rebuilding > graduated), and dispute resolution metrics.',
                path: '/credit-repair',
                roles: ['buyer', 'seller'],
            },
            {
                title: 'Stripe Payment',
                desc: 'Secure monthly subscription via Stripe Checkout. Successful payments auto-enroll clients into their chosen program tier.',
                path: '/credit-repair/intake',
                roles: ['buyer', 'seller'],
            },
        ],
    },
    {
        id: 'mortgage',
        title: 'Mortgage Lender Directory',
        icon: Building2,
        color: 'blue',
        desc: 'AI-powered mortgage readiness assessment and lender matching.',
        features: [
            {
                title: 'Readiness Assessment',
                desc: 'GPT-5.2 analyzes your credit score, DTI ratio, employment, and savings to determine mortgage readiness. Provides specific recommendations for improvement.',
                path: '/mortgage',
                roles: ['buyer'],
            },
            {
                title: 'Lender Directory',
                desc: 'Browse mortgage lenders filtered by loan type (FHA, VA, Conventional, USDA). View min credit score, DTI requirements, and contact info.',
                path: '/mortgage',
                roles: ['buyer'],
            },
            {
                title: '1003 URLA Application',
                desc: 'Start your Uniform Residential Loan Application (1003) directly in the platform. Pre-fills from your buyer profile.',
                path: '/mortgage',
                roles: ['buyer'],
            },
        ],
    },
    {
        id: 'admin',
        title: 'Administration & CRM',
        icon: BarChart3,
        color: 'purple',
        desc: 'Full admin control with automation, templates, and analytics.',
        features: [
            {
                title: 'Admin Dashboard',
                desc: 'Complete overview: buyers, sellers, deals, matches, payments, signatures. Day 91 Pipeline bar shows pending/active/agents/operators at a glance.',
                path: '/admin',
                roles: ['admin'],
            },
            {
                title: 'Search & Filters',
                desc: 'Global search across all admin tabs. Filter by status (lead, matched, negotiation, finalized, closed). Seller cards show DOM, listing status, and agent info.',
                path: '/admin',
                roles: ['admin'],
            },
            {
                title: 'CRM Automation',
                desc: '9 drip campaign templates. Auto-triggers for: enrollment, dispute sent, score update, milestones, re-engagement, Day 75 agent notification, Day 91 operator alert, dispute resolution.',
                path: '/crm',
                roles: ['admin'],
            },
            {
                title: 'Document Templates',
                desc: 'Create and edit document templates with variable interpolation. Preview with sample data. 6 defaults: agreement, dispute letter, welcome email, mortgage pre-app, payment schedule, custom.',
                path: '/templates',
                roles: ['admin'],
            },
            {
                title: 'Analytics Dashboard',
                desc: 'Day 91 funnel visualization (pending > active > matched > closed), conversion rates, operator/agent leaderboards, credit repair stats, user breakdown.',
                path: '/analytics',
                roles: ['admin'],
            },
        ],
    },
    {
        id: 'extras',
        title: 'Platform Features',
        icon: Zap,
        color: 'zinc',
        desc: 'Global features available across the entire platform.',
        features: [
            {
                title: 'AI Chatbot',
                desc: 'Available on every page. Ask about credit repair, deals, the Day 91 process, mortgage readiness, or navigate the platform. Detects seller frustration and guides to Day 91 enrollment.',
                path: null,
                roles: ['all'],
            },
            {
                title: 'Real-Time Notifications',
                desc: 'WebSocket-powered notification bell. Get instant alerts for deal updates, listing activations, buyer matches, and dispute resolutions.',
                path: null,
                roles: ['all'],
            },
            {
                title: 'Multi-Language Support',
                desc: 'Language switcher in the navbar. Built-in i18n context supports multiple languages across all pages.',
                path: null,
                roles: ['all'],
            },
            {
                title: 'Property Image Upload',
                desc: 'Upload property photos (jpg/png/webp, max 5MB) to marketplace listings. Images display in listing cards and detail views.',
                path: '/marketplace',
                roles: ['seller', 'agent'],
            },
            {
                title: 'Operator Buyer Assignment',
                desc: 'Operators assign pre-qualified buyers to active listings. Assignments tracked with status. Buyer gets notified via WebSocket and email.',
                path: '/operator',
                roles: ['operator'],
            },
        ],
    },
];

export default function GuidePage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState({});
    const [search, setSearch] = useState('');

    const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

    const filtered = SECTIONS.map(s => ({
        ...s,
        features: s.features.filter(f =>
            !search || f.title.toLowerCase().includes(search.toLowerCase()) || f.desc.toLowerCase().includes(search.toLowerCase())
        ),
    })).filter(s => s.features.length > 0);

    const roleColor = (r) => ({
        buyer: 'bg-emerald-100 text-emerald-700',
        seller: 'bg-amber-100 text-amber-700',
        agent: 'bg-blue-100 text-blue-700',
        operator: 'bg-purple-100 text-purple-700',
        admin: 'bg-zinc-800 text-white',
        all: 'bg-zinc-100 text-zinc-600',
    }[r] || 'bg-zinc-100 text-zinc-600');

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="guide-page">
            <Navbar variant="light" />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-24 pb-16">
                <div className="text-center mb-10">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-600 mb-2">Platform Guide</p>
                    <h1 className="text-3xl sm:text-4xl font-medium text-zinc-900 font-['Outfit'] mb-3" data-testid="guide-title">
                        Everything VisionAire Can Do
                    </h1>
                    <p className="text-base text-zinc-500 max-w-xl mx-auto">
                        A complete directory of every feature, tool, and automation in the platform — organized by function.
                    </p>
                </div>

                {/* Search */}
                <div className="relative max-w-md mx-auto mb-10">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input type="text" placeholder="Search features..." value={search} onChange={e => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-zinc-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                        data-testid="guide-search" />
                </div>

                {/* Quick Role Guide */}
                {!search && (
                    <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm mb-8" data-testid="quick-start">
                        <h2 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-4">Quick Start by Role</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { role: 'Buyer', steps: ['Register as Buyer', 'Complete financial intake', 'Get pre-qualified', 'View matched properties'], icon: Users, color: 'emerald' },
                                { role: 'Seller', steps: ['Register as Seller', 'Submit property + agent info', 'Wait for Day 91 activation', 'Get matched with buyers'], icon: Home, color: 'amber' },
                                { role: 'Agent', steps: ['Register as Agent', 'Enroll listings at Day 75+', 'Track activation status', 'Earn on second-market deals'], icon: Briefcase, color: 'blue' },
                                { role: 'Operator', steps: ['Register as Operator', 'View Day 91+ listings', 'Assign qualified buyers', 'Close deals'], icon: Target, color: 'purple' },
                            ].map(r => (
                                <div key={r.role} className={`bg-${r.color}-50 border border-${r.color}-100 rounded-xl p-4`}>
                                    <r.icon className={`w-5 h-5 text-${r.color}-600 mb-2`} />
                                    <div className={`text-sm font-semibold text-${r.color}-700 mb-2`}>{r.role}</div>
                                    <ol className="text-[11px] text-zinc-600 space-y-1">
                                        {r.steps.map((s, i) => (
                                            <li key={i} className="flex items-start gap-1.5">
                                                <span className={`w-4 h-4 rounded-full bg-${r.color}-100 text-${r.color}-700 text-[9px] font-bold flex items-center justify-center shrink-0 mt-0.5`}>{i + 1}</span>
                                                {s}
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Feature Directory */}
                <div className="space-y-4">
                    {filtered.map(section => {
                        const isOpen = expanded[section.id] !== false; // default open
                        const Icon = section.icon;
                        return (
                            <div key={section.id} className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden"
                                data-testid={`guide-section-${section.id}`}>
                                <button onClick={() => toggle(section.id)}
                                    className="w-full flex items-center gap-3 p-5 text-left hover:bg-zinc-50 transition-colors">
                                    <div className={`w-10 h-10 rounded-xl bg-${section.color}-100 flex items-center justify-center`}>
                                        <Icon className={`w-5 h-5 text-${section.color}-600`} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-base font-medium text-zinc-900 font-['Outfit']">{section.title}</div>
                                        <div className="text-xs text-zinc-500 mt-0.5">{section.desc}</div>
                                    </div>
                                    <Badge className="bg-zinc-100 text-zinc-600 text-[10px] mr-2">{section.features.length} features</Badge>
                                    {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                                </button>
                                {isOpen && (
                                    <div className="px-5 pb-5 space-y-3">
                                        {section.features.map((f, i) => (
                                            <div key={i} className="bg-zinc-50 rounded-xl p-4 flex items-start gap-3 group">
                                                <div className="w-6 h-6 rounded-lg bg-white border border-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-400 shrink-0 mt-0.5">
                                                    {i + 1}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium text-zinc-900 mb-1">{f.title}</div>
                                                    <div className="text-xs text-zinc-600 leading-relaxed mb-2">{f.desc}</div>
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        {f.roles.map(r => (
                                                            <Badge key={r} className={`${roleColor(r)} text-[9px]`}>{r}</Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                                {f.path && user && (
                                                    <Button size="sm" variant="ghost" onClick={() => navigate(f.path)}
                                                        className="text-xs text-emerald-600 hover:text-emerald-700 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        Go <ArrowRight className="w-3 h-3 ml-1" />
                                                    </Button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* YouTube Embed at Bottom */}
                <div className="mt-10 bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="guide-video">
                    <div className="aspect-video">
                        <video
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/visionaire_explainer_final.mp4?v=2`}
                            title="VisionAire Realty Services - The Day 91 Concept"
                            controls
                            muted
                            
                            playsInline
                                poster={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/visionaire_poster.jpg?v=2`}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="p-5 text-center">
                        <div className="text-sm font-medium text-zinc-900">Watch: The Day 91 Concept Explained</div>
                        <div className="text-xs text-zinc-500 mt-1">Understand how VisionAire creates opportunities where traditional methods fail.</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
